import dayjs from "dayjs";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { useParams, useSearchParams } from "react-router";
import { ResizeHandle } from "@/components/ui/ResizeHandle";
import { useResizableColumn } from "@/components/ui/useResizableColumn";
import { usePageHeaderSlot } from "@/layout/PageHeaderSlotContext";
import { RelevanceReviewControl } from "@/relevance-review/RelevanceReviewControl";
import { RelevanceStatusExplanation } from "@/relevance-review/RelevanceStatusExplanation";
import {
  resolveBundleRelevanceStatus,
  useBundleRelevanceStatusesForEncounter,
} from "@/relevance-review/useBundleRelevanceStatusesForEncounter";
import { BundleFileTabs } from "./BundleFileTabs";
import { BundleTabs } from "./BundleTabs";
import { ProcessedDocumentTabs } from "./ProcessedDocumentTabs";
import { useBundlesForPatient } from "./useBundlesForPatient";
import { useEncounterById } from "./useEncounterById";
import { useProcessedDocumentsForEncounter } from "./useProcessedDocumentsForEncounter";
import { useSkippedPagesForEncounter } from "./useSkippedPagesForEncounter";

const BUNDLE_PARAM = "bundle";

export default function RawFilesViewerEncounterPage() {
  const { encounterId } = useParams<{ encounterId: string }>();
  const pageHeaderSlot = usePageHeaderSlot();
  const {
    data: encounter,
    isLoading: isLoadingEncounter,
    error: encounterError,
  } = useEncounterById(encounterId ?? null);

  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data: bundles = [],
    isLoading: isLoadingBundles,
    error: bundlesError,
  } = useBundlesForPatient(encounter?.patient_id ?? null);
  const selectedBundle = bundles.find((b) => b.id === searchParams.get(BUNDLE_PARAM)) ?? bundles[0] ?? null;
  const selectedBundleId = selectedBundle?.id ?? null;
  const selectBundle = (bundleId: string) =>
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set(BUNDLE_PARAM, bundleId);
        return next;
      },
      { replace: true },
    );

  const {
    data: processedDocumentsResult,
    isLoading: isLoadingProcessedDocuments,
    error: processedDocumentsError,
  } = useProcessedDocumentsForEncounter(encounter?.id ?? null);

  const { data: skippedPages } = useSkippedPagesForEncounter(encounter?.id ?? null);

  const bundleRelevanceStatusesQuery = useBundleRelevanceStatusesForEncounter(encounter?.id ?? null);
  const selectedBundleStatus = selectedBundleId
    ? resolveBundleRelevanceStatus(selectedBundleId, bundleRelevanceStatusesQuery)
    : null;

  const processedDocumentsColumn = useResizableColumn({
    initial: 30,
    min: 20,
    max: 50,
    storageKey: "raw-files-viewer-procedure-histology-column-width",
    unit: "percent",
    invert: true,
  });

  const bundleListColumn = useResizableColumn({
    initial: 288,
    min: 200,
    max: 500,
    storageKey: "raw-files-viewer-bundle-list-column-width",
    unit: "pixel",
  });

  const headerSlotEl =
    pageHeaderSlot?.slotReady && pageHeaderSlot.headerSlotRef.current ? pageHeaderSlot.headerSlotRef.current : null;

  const renderHeader = (details?: ReactNode) => (
    <div className="flex items-center gap-3 text-sm text-primary-foreground">
      <h1 className="font-semibold">Raw Files Viewer</h1>
      {details && <span className="text-primary-foreground/80">{details}</span>}
    </div>
  );

  const header = (details?: ReactNode) =>
    headerSlotEl ? (
      createPortal(renderHeader(details), headerSlotEl)
    ) : (
      <div className="flex items-center gap-3 border-b pb-3 text-sm bg-primary text-primary-foreground -mx-6 -mt-6 px-6 py-3">
        {renderHeader(details)}
      </div>
    );

  if (isLoadingEncounter) {
    return (
      <div className="p-6 space-y-3">
        {header()}
        <div className="text-sm text-muted-foreground">Loading encounter…</div>
      </div>
    );
  }

  if (encounterError || !encounter) {
    return (
      <div className="p-6 space-y-3">
        {header()}
        <div className="text-destructive">
          {encounterError ? encounterError.message : "No encounter found for that id."}
        </div>
      </div>
    );
  }

  const encounterDetails = (
    <>
      Patient <span className="font-mono">{encounter.patient_id}</span> · Encounter{" "}
      <span className="font-mono">{encounter.id}</span> · {encounter.specialty} · Discharged{" "}
      {encounter.discharge_datetime ? dayjs(encounter.discharge_datetime).format("DD/MM/YYYY HH:mm") : "—"}
    </>
  );

  return (
    <div className="p-6 space-y-3">
      {header(encounterDetails)}

      <div className="flex gap-6">
        <div style={{ width: `${bundleListColumn.width}px` }} className="shrink-0">
          <BundleTabs
            bundles={bundles}
            isLoading={isLoadingBundles}
            error={bundlesError}
            selectedBundleId={selectedBundleId}
            onSelectBundle={selectBundle}
            relevanceStatuses={bundleRelevanceStatusesQuery}
            skippedPages={skippedPages}
          />
        </div>

        <ResizeHandle
          onMouseDown={bundleListColumn.handleMouseDown}
          isDragging={bundleListColumn.isDragging}
          ariaLabel="Resize bundle list column"
        />

        <div className="flex-1 min-w-[200px] space-y-3">
          {selectedBundle && bundleRelevanceStatusesQuery.error && !bundleRelevanceStatusesQuery.data && (
            <div className="rounded-md border p-3 text-sm text-destructive">Relevance status unavailable</div>
          )}
          {selectedBundle &&
            selectedBundleStatus &&
            (selectedBundleStatus.kind === "pending-review" || selectedBundleStatus.kind === "reviewed" ? (
              <RelevanceReviewControl
                key={`control-${selectedBundleStatus.reviewItem.id}`}
                encounterId={encounter.id}
                reviewItem={selectedBundleStatus.reviewItem}
              />
            ) : (
              <RelevanceStatusExplanation key={`explanation-${selectedBundle.id}`} status={selectedBundleStatus} />
            ))}
          {selectedBundle && (
            <BundleFileTabs
              key={`files-${selectedBundle.id}`}
              bundleId={selectedBundle.id}
              files={selectedBundle.files}
              skippedPagesByFile={skippedPages?.get(selectedBundle.id)}
            />
          )}
        </div>

        <ResizeHandle
          onMouseDown={processedDocumentsColumn.handleMouseDown}
          isDragging={processedDocumentsColumn.isDragging}
          ariaLabel="Resize procedure/histology column"
        />

        <div style={{ width: `${processedDocumentsColumn.width}%` }} className="min-w-[250px] shrink-0">
          <ProcessedDocumentTabs
            key={encounter.id}
            documents={processedDocumentsResult?.documents ?? []}
            isLoading={isLoadingProcessedDocuments}
            error={processedDocumentsError}
            failedCount={processedDocumentsResult?.failedCount ?? 0}
          />
        </div>
      </div>
    </div>
  );
}
